/**
 * Gideon campaign lie skins — themed claims for Judges 6–7.
 * Mechanics stay on base lieId; presentation uses these.
 */
import type { LieSpawnSkin } from "../../game/cards/types";

export type GideonLieType = "fear" | "despair" | "doubt";

export interface GideonLieSkin extends LieSpawnSkin {
  voId: string;
  artId: string;
  type: GideonLieType;
  claims: { 1: string; 2: string; 3: string };
}

function skin(
  voId: string,
  name3: string,
  claim3: string,
  claim2: string,
  claim1: string,
  artId: string,
  type: GideonLieType,
  names?: { n2?: string; n1?: string },
): GideonLieSkin {
  return {
    voId,
    artId,
    type,
    name: name3,
    names: {
      3: name3,
      2: names?.n2 ?? name3,
      1: names?.n1 ?? name3,
    },
    claim: claim3,
    claims: { 3: claim3, 2: claim2, 1: claim1 },
  };
}

/**
 * Dedicated Gideon art plates (shipped as /art/lies/{artId}-{1|2|3}.webp).
 * Multiple claim skins can share a plate family (same pattern as Nehemiah).
 */
export const GIDEON_ART_IDS = [
  "gid-if-with-us",
  "gid-least",
  "gid-hide",
  "gid-need-sign",
  "gid-holy-dread",
  "gid-city",
  "gid-baal",
  "gid-host",
  "gid-remnant",
  "gid-barley",
] as const;

export const G1 = {
  ifWithUs: skin(
    "gid-if-with-us",
    "If With Us",
    "If the Lord be with us, why then is all this befallen us?",
    "Where be all his miracles which our fathers told us of?",
    "Has He forsaken us?",
    "gid-if-with-us",
    "doubt",
    { n2: "Where Are the Miracles?", n1: "Forsaken?" },
  ),
  leastInHouse: skin(
    "gid-least",
    "The Least",
    "Wherewith shall I save Israel? I am the least in my father's house.",
    "My family is poor in Manasseh.",
    "I am too small.",
    "gid-least",
    "fear",
    { n2: "Poor Clan", n1: "Too Small" },
  ),
  midianHides: skin(
    "gid-hide-wheat",
    "Hide the Wheat",
    "Thresh in secret — or Midian takes everything.",
    "They will find the grain.",
    "Stay hidden.",
    "gid-hide",
    "fear",
  ),
};

export const G2 = {
  needASign: skin(
    "gid-need-sign",
    "Shew Me a Sign",
    "Unless I see a sign, I cannot trust this word.",
    "Is this really from the Lord?",
    "One more confirmation…",
    "gid-need-sign",
    "doubt",
    { n2: "Is This Real?", n1: "Still Unsure" },
  ),
  fearDeath: skin(
    "gid-face-to-face",
    "Face to Face",
    "I have seen an angel of the Lord face to face — I will die.",
    "This is too holy; I am undone.",
    "Fear lingers after the fire.",
    "gid-holy-dread",
    "fear",
  ),
};

export const G3 = {
  cityWillKill: skin(
    "gid-city-kill",
    "The City Will Kill You",
    "If you tear down Baal, the men of the city will kill you.",
    "Your own house will turn on you.",
    "Obedience is too costly.",
    "gid-city",
    "fear",
    { n2: "House Against You", n1: "Too Costly" },
  ),
  nightOnly: skin(
    "gid-by-night",
    "Only by Night",
    "You cannot do this in the day — fear owns the daylight.",
    "Hide the work.",
    "Wait for darkness.",
    "gid-hide",
    "fear",
  ),
  baalStands: skin(
    "gid-baal-stands",
    "Baal Stands",
    "Baal will answer for himself — you have gained nothing.",
    "The grove will grow back.",
    "Idols always return.",
    "gid-baal",
    "despair",
  ),
};

export const G4 = {
  grasshopperHost: skin(
    "gid-grasshoppers",
    "As Grasshoppers",
    "They come as grasshoppers for multitude — camels without number.",
    "The valley is full of them.",
    "You cannot count the host.",
    "gid-host",
    "fear",
    { n2: "Valley Full", n1: "Uncountable" },
  ),
  cannotStand: skin(
    "gid-cannot-stand",
    "Cannot Stand",
    "You cannot stand in the open against Midian.",
    "Stay in dens and caves.",
    "The open field is death.",
    "gid-host",
    "fear",
  ),
  withoutNumber: skin(
    "gid-without-number",
    "Without Number",
    "Both they and their camels were without number.",
    "East and Amalek with them.",
    "The land is swallowed.",
    "gid-host",
    "despair",
  ),
};

export const G5 = {
  proveIt: skin(
    "gid-prove-it",
    "Prove It",
    "If thou wilt save Israel by mine hand, as thou hast said — prove it.",
    "Let the fleece decide.",
    "I need to know.",
    "gid-need-sign",
    "doubt",
    { n2: "Let the Fleece Decide", n1: "I Need to Know" },
  ),
  onceMore: skin(
    "gid-once-more",
    "But This Once",
    "Let not thine anger be hot against me — let me prove but this once.",
    "Reverse the sign; I still tremble.",
    "One more night.",
    "gid-need-sign",
    "doubt",
    { n2: "Reverse the Sign", n1: "One More Night" },
  ),
};

export const G6 = {
  tooFew: skin(
    "gid-too-few",
    "Too Few",
    "We are too few. This remnant cannot save anyone.",
    "Twenty-two thousand left — and still too many for pride.",
    "Three hundred against a host?",
    "gid-remnant",
    "despair",
    { n2: "Still Too Many for Pride", n1: "Three Hundred?" },
  ),
  fearfulLeave: skin(
    "gid-fearful-leave",
    "Fearful and Afraid",
    "Whosoever is fearful and afraid, let him return.",
    "The ranks thin.",
    "Even courage walks away.",
    "gid-remnant",
    "fear",
  ),
  ownHand: skin(
    "gid-own-hand",
    "Mine Own Hand",
    "You will vaunt yourselves: Mine own hand hath saved me.",
    "Glory wants the credit.",
    "Numbers feel like salvation.",
    "gid-if-with-us",
    "doubt",
    { n2: "Glory Wants Credit", n1: "Numbers as Salvation" },
  ),
};

export const G7 = {
  lookAtTheHost: skin(
    "gid-look-host",
    "Look at the Host",
    "Even now you will fail. Look at the host in the valley.",
    "Camels as the sand by the sea.",
    "The night is thick with them.",
    "gid-host",
    "fear",
  ),
  ifThouFear: skin(
    "gid-if-thou-fear",
    "If Thou Fear",
    "If thou fear to go down — you are still that man under the winepress.",
    "Take Phurah and listen.",
    "Fear has a name.",
    "gid-if-with-us",
    "doubt",
    { n2: "Take Phurah", n1: "Fear Has a Name" },
  ),
  barleyEcho: skin(
    "gid-barley-echo",
    "Barley Cake",
    "A cake of barley against a host — it cannot be.",
    "The dream is only a dream.",
    "Strength fades after worship.",
    "gid-barley",
    "despair",
  ),
};

export const ALL_GIDEON_LIE_SKINS: GideonLieSkin[] = [
  ...Object.values(G1),
  ...Object.values(G2),
  ...Object.values(G3),
  ...Object.values(G4),
  ...Object.values(G5),
  ...Object.values(G6),
  ...Object.values(G7),
];
